import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ProductImageGallery = ({ images = [], productName = '' }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomModalVisible, setZoomModalVisible] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [zoomImageLoading, setZoomImageLoading] = useState(true);
  const [touchStartX, setTouchStartX] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const scrollViewRef = useRef(null);
  const zoomImageRef = useRef(null);
  
  // Détection desktop
  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    
    return () => {
      window.removeEventListener('resize', checkDesktop);
    };
  }, []);

  // Normaliser les images
  const normalizedImages = React.useMemo(() => {
    if (!images || images.length === 0) return [];
    
    return images.map((image, index) => {
      if (image && typeof image === 'object') {
        if (image.photo_url) {
          return {
            url: image.photo_url,
            alt: image.description || `${productName} - Image ${index + 1}`
          };
        } else if (image.url) {
          return {
            url: image.url,
            alt: image.alt || image.description || `${productName} - Image ${index + 1}`
          };
        }
      }
      
      if (typeof image === 'string') {
        return {
          url: image,
          alt: `${productName} - Image ${index + 1}`
        };
      }
      
      return {
        url: 'https://img.freepik.com/free-vector/illustration-gallery-icon_53876-27002.jpg',
        alt: `${productName} - Image ${index + 1}`
      };
    }).filter(img => img.url);
  }, [images, productName]);

  // Gestion des événements tactiles pour le swipe
  const handleTouchStart = (e) => {
    if (!isDesktop) {
      setTouchStartX(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null || isDesktop) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    const swipeThreshold = 50;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0 && currentImageIndex < normalizedImages.length - 1) {
        handleNextImage();
      } else if (diff < 0 && currentImageIndex > 0) {
        handlePrevImage();
      }
    }
    
    setTouchStartX(null);
  };

  // Gestion des événements clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (zoomModalVisible) {
        if (e.key === 'Escape') {
          setZoomModalVisible(false);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          handlePrevImage();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          handleNextImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomModalVisible, currentImageIndex, normalizedImages.length]);

  const handleNextImage = () => {
    if (currentImageIndex < normalizedImages.length - 1) {
      const newIndex = currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      setImageLoading(true);
      
      // Scroll vers la miniature suivante
      if (scrollViewRef.current && normalizedImages.length > 4) {
        const thumbnailElement = scrollViewRef.current.querySelector(
          `[data-index="${newIndex}"]`
        );
        if (thumbnailElement) {
          thumbnailElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    }
  };

  const handlePrevImage = () => {
    if (currentImageIndex > 0) {
      const newIndex = currentImageIndex - 1;
      setCurrentImageIndex(newIndex);
      setImageLoading(true);
      
      // Scroll vers la miniature précédente
      if (scrollViewRef.current && normalizedImages.length > 4) {
        const thumbnailElement = scrollViewRef.current.querySelector(
          `[data-index="${newIndex}"]`
        );
        if (thumbnailElement) {
          thumbnailElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    }
  };

  const toggleZoomModal = () => {
    setZoomModalVisible(!zoomModalVisible);
    if (!zoomModalVisible) {
      setZoomImageLoading(true);
    }
  };

  const handleThumbnailPress = (index) => {
    setCurrentImageIndex(index);
    setImageLoading(true);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleZoomImageLoad = () => {
    setZoomImageLoading(false);
  };

  // Gestion du zoom avec molette de souris (desktop)
  const handleWheelZoom = (e) => {
    if (!zoomModalVisible || !isDesktop) return;
    
    e.preventDefault();
    const zoomImage = zoomImageRef.current;
    if (!zoomImage) return;
    
    const zoomSpeed = 0.1;
    const delta = e.deltaY > 0 ? -zoomSpeed : zoomSpeed;
    
    const currentTransform = zoomImage.style.transform || 'scale(1)';
    const currentScale = parseFloat(currentTransform.match(/scale\(([^)]+)\)/)?.[1] || 1);
    const newScale = Math.min(Math.max(currentScale + delta, 0.5), 3);
    
    zoomImage.style.transform = `scale(${newScale})`;
    zoomImage.style.transition = 'transform 0.2s ease';
  };

  // URL et description de l'image actuelle
  const currentImage = normalizedImages[currentImageIndex];
  const currentImageUrl = currentImage?.url;
  const currentImageAlt = currentImage?.alt || '';

  if (normalizedImages.length === 0) {
    return (
      <div className="bg-gray-100 rounded-xl aspect-square flex flex-col items-center justify-center border border-gray-200">
        <Icon name="ImageOff" size={48} color="#9ca3af" />
        <p className="text-gray-500 font-medium mt-2">Aucune image disponible</p>
      </div>
    );
  }

  return (
    <div className="mb-4">
      {/* Image principale - Layout différent desktop/mobile */}
      <div className={`relative bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 ${
        isDesktop ? 'max-w-4xl mx-auto' : ''
      }`}>
        <div 
          className={`relative ${isDesktop ? 'aspect-video' : 'aspect-square'} cursor-zoom-in group`}
          onClick={toggleZoomModal}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheelZoom}
        >
          {imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          )}
          
          {currentImageUrl ? (
            <img
              src={currentImageUrl}
              alt={currentImageAlt}
              className={`w-full h-full object-cover transition-all duration-300 ${
                imageLoading ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
              } ${isDesktop ? 'group-hover:scale-105' : ''}`}
              onLoad={handleImageLoad}
              onError={(e) => {
                setImageLoading(false);
                e.target.src = 'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=800&auto=format&fit=crop';
              }}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
              <Icon name="ImageOff" size={48} color="#9ca3af" />
              <p className="text-gray-500 mt-2">Image non disponible</p>
            </div>
          )}
          
          {/* Indicateur de zoom */}
          <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm flex items-center gap-2 px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Icon name="Maximize2" size={16} color="white" />
            <span className="text-white text-sm font-semibold">Zoom</span>
          </div>

          {/* Compteur d'images */}
          <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full">
            <span className="text-white text-sm font-semibold">
              {currentImageIndex + 1} / {normalizedImages.length}
            </span>
          </div>

          {/* Flèches de navigation - Visibles sur desktop, sur hover sur mobile */}
          {normalizedImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevImage();
                }}
                disabled={currentImageIndex === 0}
                className={`absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center transition-all ${
                  currentImageIndex === 0 
                    ? 'opacity-40 cursor-not-allowed' 
                    : 'hover:bg-black/80 active:scale-95 opacity-0 group-hover:opacity-100 md:opacity-100'
                }`}
              >
                <Icon name="ChevronLeft" size={24} color="white" />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
                disabled={currentImageIndex === normalizedImages.length - 1}
                className={`absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center transition-all ${
                  currentImageIndex === normalizedImages.length - 1
                    ? 'opacity-40 cursor-not-allowed' 
                    : 'hover:bg-black/80 active:scale-95 opacity-0 group-hover:opacity-100 md:opacity-100'
                }`}
              >
                <Icon name="ChevronRight" size={24} color="white" />
              </button>
            </>
          )}

          {/* Boutons d'action desktop */}
          {isDesktop && normalizedImages.length > 1 && (
            <div className="absolute bottom-4 right-4 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevImage();
                }}
                disabled={currentImageIndex === 0}
                className={`px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg flex items-center gap-2 transition-all ${
                  currentImageIndex === 0 
                    ? 'opacity-40 cursor-not-allowed' 
                    : 'hover:bg-white shadow-md'
                }`}
              >
                <Icon name="ChevronLeft" size={18} color="#374151" />
                <span className="text-gray-800 font-medium text-sm">Précédent</span>
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
                disabled={currentImageIndex === normalizedImages.length - 1}
                className={`px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg flex items-center gap-2 transition-all ${
                  currentImageIndex === normalizedImages.length - 1
                    ? 'opacity-40 cursor-not-allowed' 
                    : 'hover:bg-white shadow-md'
                }`}
              >
                <span className="text-gray-800 font-medium text-sm">Suivant</span>
                <Icon name="ChevronRight" size={18} color="#374151" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation par miniatures */}
      {normalizedImages.length > 1 && (
        <div className={`mt-4 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
          <div 
            ref={scrollViewRef}
            className={`flex gap-3 overflow-x-auto pb-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 ${
              isDesktop ? 'justify-center' : ''
            }`}
          >
            {normalizedImages.map((image, index) => (
              <button
                key={index}
                data-index={index}
                onClick={() => handleThumbnailPress(index)}
                className={`flex-shrink-0 transition-all duration-200 ${
                  isDesktop ? 'w-20 h-20' : 'w-16 h-16'
                } rounded-lg overflow-hidden border-2 ${
                  index === currentImageIndex 
                    ? 'border-blue-500 ring-2 ring-blue-200 shadow-md scale-105' 
                    : 'border-transparent hover:border-gray-300 hover:shadow'
                }`}
              >
                <img
                  src={image.url}
                  alt={`${productName} - Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=200&auto=format&fit=crop';
                  }}
                />
              </button>
            ))}
          </div>

          {/* Points de navigation - Optionnel sur desktop */}
          {!isDesktop && (
            <div className="flex justify-center gap-2 mt-3">
              {normalizedImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleThumbnailPress(index)}
                  className={`transition-all duration-200 ${
                    index === currentImageIndex
                      ? 'w-2.5 h-2.5 bg-blue-500 rounded-full'
                      : 'w-2 h-2 bg-gray-300 rounded-full hover:bg-gray-400'
                  }`}
                  aria-label={`Aller à l'image ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal de zoom */}
      {zoomModalVisible && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={toggleZoomModal}
        >
          <div 
            className={`relative ${isDesktop ? 'w-full max-w-6xl' : 'w-full'} max-h-[90vh]`}
            onClick={(e) => e.stopPropagation()}
          >
            {zoomImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            )}
            
            {currentImageUrl ? (
              <img
                ref={zoomImageRef}
                src={currentImageUrl}
                alt={currentImageAlt}
                className={`w-full h-full object-contain mx-auto transition-transform duration-200 ${
                  zoomImageLoading ? 'opacity-0' : 'opacity-100'
                } ${isDesktop ? 'max-h-[80vh]' : 'max-h-[70vh]'}`}
                onLoad={handleZoomImageLoad}
                onError={(e) => {
                  setZoomImageLoading(false);
                  e.target.src = 'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?w=1200&auto=format&fit=crop';
                }}
              />
            ) : (
              <div className="w-full h-[70vh] flex flex-col items-center justify-center">
                <Icon name="ImageOff" size={64} color="#9ca3af" />
                <p className="text-gray-300 mt-3">Image non disponible</p>
              </div>
            )}
            
            {/* En-tête du modal */}
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-white text-lg font-medium truncate pr-6">
                    {currentImageAlt}
                  </p>
                  <p className="text-gray-300 text-sm mt-1">
                    {currentImageIndex + 1} sur {normalizedImages.length} images
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {isDesktop && (
                    <>
                      <button
                        onClick={handlePrevImage}
                        disabled={currentImageIndex === 0}
                        className={`px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg flex items-center gap-2 transition-all ${
                          currentImageIndex === 0
                            ? 'opacity-40 cursor-not-allowed'
                            : 'hover:bg-white/30'
                        }`}
                      >
                        <Icon name="ChevronLeft" size={18} color="white" />
                        <span className="text-white text-sm">Précédent</span>
                      </button>
                      
                      <button
                        onClick={handleNextImage}
                        disabled={currentImageIndex === normalizedImages.length - 1}
                        className={`px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg flex items-center gap-2 transition-all ${
                          currentImageIndex === normalizedImages.length - 1
                            ? 'opacity-40 cursor-not-allowed'
                            : 'hover:bg-white/30'
                        }`}
                      >
                        <span className="text-white text-sm">Suivant</span>
                        <Icon name="ChevronRight" size={18} color="white" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={toggleZoomModal}
                    className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors ml-2"
                    aria-label="Fermer"
                  >
                    <Icon name="X" size={24} color="white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Boutons de contrôle (en bas) */}
            {normalizedImages.length > 1 && (
              <div className={`absolute ${
                isDesktop ? 'bottom-6 left-1/2 -translate-x-1/2' : 'bottom-4 left-1/2 -translate-x-1/2'
              } flex items-center gap-4 bg-black/60 backdrop-blur-sm rounded-full px-6 py-3`}>
                <button
                  onClick={handlePrevImage}
                  disabled={currentImageIndex === 0}
                  className={`p-2 rounded-full transition-all ${
                    currentImageIndex === 0
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:bg-white/20'
                  }`}
                  aria-label="Image précédente"
                >
                  <Icon name="ChevronLeft" size={24} color="white" />
                </button>
                
                <div className="text-center">
                  <span className="text-white text-base font-semibold px-4">
                    {currentImageIndex + 1} / {normalizedImages.length}
                  </span>
                  {isDesktop && (
                    <p className="text-gray-300 text-xs mt-1">
                      Utilisez ← → ou la molette pour naviguer
                    </p>
                  )}
                </div>
                
                <button
                  onClick={handleNextImage}
                  disabled={currentImageIndex === normalizedImages.length - 1}
                  className={`p-2 rounded-full transition-all ${
                    currentImageIndex === normalizedImages.length - 1
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:bg-white/20'
                  }`}
                  aria-label="Image suivante"
                >
                  <Icon name="ChevronRight" size={24} color="white" />
                </button>
              </div>
            )}

            {/* Contrôles de zoom pour desktop */}
            {isDesktop && (
              <div className="absolute bottom-6 right-6 flex flex-col gap-2">
                <button
                  onClick={() => {
                    const zoomImage = zoomImageRef.current;
                    if (zoomImage) {
                      const currentTransform = zoomImage.style.transform || 'scale(1)';
                      const currentScale = parseFloat(currentTransform.match(/scale\(([^)]+)\)/)?.[1] || 1);
                      const newScale = Math.min(currentScale + 0.5, 3);
                      zoomImage.style.transform = `scale(${newScale})`;
                      zoomImage.style.transition = 'transform 0.2s ease';
                    }
                  }}
                  className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
                  aria-label="Zoom avant"
                >
                  <Icon name="ZoomIn" size={24} color="white" />
                </button>
                
                <button
                  onClick={() => {
                    const zoomImage = zoomImageRef.current;
                    if (zoomImage) {
                      const currentTransform = zoomImage.style.transform || 'scale(1)';
                      const currentScale = parseFloat(currentTransform.match(/scale\(([^)]+)\)/)?.[1] || 1);
                      const newScale = Math.max(currentScale - 0.5, 0.5);
                      zoomImage.style.transform = `scale(${newScale})`;
                      zoomImage.style.transition = 'transform 0.2s ease';
                    }
                  }}
                  className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
                  aria-label="Zoom arrière"
                >
                  <Icon name="ZoomOut" size={24} color="white" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Styles CSS personnalisés */}
      <style jsx>{`
        .scrollbar-thin::-webkit-scrollbar {
          height: 6px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        
        /* Animation pour le zoom */
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        .modal-enter {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ProductImageGallery;